import type { HistoricalTrade } from 'models/forms/AddonManagementFormResult';
import { createSelector } from 'reselect';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import { ExposedFormStatus } from 'state/forms/models';
import type { AppState } from 'state/state.model';
import { isTradeEnabledForUser } from 'state/user/user.selector';

import {
  canExecuteInAddonManagementWorkspace,
  canPriceInAddonManagementWorkspace,
  isReadOnlyForm,
  translateAddonManagementFormStatus,
} from '../addonManagementForm.helpers';
import type { AddonManagementFormInfos } from './infos.model';

export function getAddonManagementFormsState(
  appState: AppState,
): Readonly<Record<string, Readonly<AddonManagementFormInfos>>> {
  return appState.addonManagementForm?.infos;
}
export function getForm(state: AppState, formId: string): AddonManagementFormInfos | undefined {
  return state.addonManagementForm?.infos[formId];
}

export function getHistoricalTrades(state: AppState, formId: string): HistoricalTrade[] {
  return getForm(state, formId)?.historicalTrades ?? [];
}

export function getPrimaryTrade(state: AppState, formId: string): HistoricalTrade | undefined {
  const historicalTrades = getHistoricalTrades(state, formId);
  return historicalTrades?.find(trade => trade.status === 'Booked') ?? undefined;
}

export function getAddonManagementFormLastQuoteId(state: AppState, formId: string) {
  return getForm(state, formId)?.lastQuoteId;
}

export function getAddonManagementSelectedForms(appState: AppState): readonly AddonManagementFormInfos[] {
  const forms = getAddonManagementFormsState(appState);
  return Object.values(forms).filter(form => form.isSelected);
}

export function getAddonManagementSelectedFormsLength(appState: AppState): number {
  const forms = getAddonManagementFormsState(appState);
  return Object.values(forms).filter(form => form.isSelected).length;
}

export function getAddonManagementFormById(appState: AppState, formId: string): AddonManagementFormInfos | undefined {
  const state = getAddonManagementFormsState(appState);
  return state[formId];
}

export function isAddonManagementFormSelected(state: AppState, formId: string): boolean {
  const form = getForm(state, formId);
  return form ? form.isSelected : false;
}

export function areAllAddonManagementFormsSelected(state: AppState) {
  const allFormIds = getFormIdsInAddonManagement(state);
  return !allFormIds.some(id => !isAddonManagementFormSelected(state, id));
}

export function isNoAddonManagementFormSelected(state: AppState) {
  const allFormIds = getFormIdsInAddonManagement(state);
  return !allFormIds.some(id => isAddonManagementFormSelected(state, id));
}

export const getFormIdsInAddonManagement = createSelector(
  (state: AppState) => state.addonManagementForm?.infos,
  info => Object.keys(info),
);

export function getSelectedFormIdsInAddonManagement(appState: AppState) {
  const formIds: string[] = getFormIdsInAddonManagement(appState);
  return formIds.filter(formId => {
    const formInfos = getForm(appState, formId);
    return formInfos?.isSelected;
  });
}

export function getAddonManagementQuoteId(state: AppState, formId: string): string | undefined {
  const infos = getForm(state, formId);
  return infos ? infos.quoteId : undefined;
}

export function getAddonManagementFormStatusById(state: AppState, formId: string): ExposedFormStatus {
  const form = getForm(state, formId);
  return translateAddonManagementFormStatus(form ? form.status : 'NOT_READY');
}

export function isQuotingAddon(state: AppState) {
  const formIds = getFormIdsInAddonManagement(state);

  return formIds.some(formId => {
    const status = getAddonManagementFormStatusById(state, formId);
    return status === 'Quoting' || status === 'Executing';
  });
}

export function isSelectedFormsQuoted(state: AppState) {
  const forms = getAddonManagementSelectedForms(state);

  return forms.length > 0 ? forms.some(form => form.status === 'QUOTED') : false;
}

export function canAddonManagementSelectedFormsBePriced(appState: AppState): boolean {
  const forms = getAddonManagementSelectedForms(appState);
  return forms.length > 0 ? forms.every(form => canPriceInAddonManagementWorkspace(form.status)) : false;
}

export function canAddonManagementSelectedFormsBeExecuted(appState: AppState): boolean {
  const forms = getAddonManagementSelectedForms(appState);
  return forms.length > 0
    ? forms.every(form => canExecuteInAddonManagementWorkspace(form.status)) && isTradeEnabledForUser(appState)
    : false;
}

const emptyArray: readonly [] = [];

export function getAddonManagementValidationErrors(appState: AppState, formId: string): readonly QuoteFormError[] {
  const form = getAddonManagementFormById(appState, formId);
  return form?.validationErrors ?? emptyArray;
}

export function isAddonManagementFormReadOnly(appState: AppState, formId: string) {
  const form = getForm(appState, formId);
  if (form !== undefined && isReadOnlyForm(form.status)) {
    return true;
  }
  return false;
}
